import {
  Box,
  Flex,
  Avatar,
  HStack,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  useDisclosure,
  useColorModeValue,
  useColorMode,
} from '@chakra-ui/react'
import { HamburgerIcon, ArrowBackIcon } from '@chakra-ui/icons'
import { useUserContext } from '../utils/userContext'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import React, { useCallback } from 'react'
import { SettingsIcon } from '@chakra-ui/icons'
import Drawer from "../components/Drawer";
import PermissionGuard from './security/PermissionGuard'

interface DropdownMenuItemProps {
  action: () => void;
  icon: JSX.Element;
  name: string
}

const DropdownMenuItem = (props: DropdownMenuItemProps) => {
  return (
    <MenuItem onClick={() => props.action()}>
      {props.icon}
      <div className='px-2'>
        {props.name}
      </div>
    </MenuItem>
  )
}

export interface NavLinkProp {
  name: string;
  link: string;
  icon: JSX.Element;
  permissions: string[];
};

const NavLink = (props: NavLinkProp) => {
  const nav = useNavigate();
  const { t } = useTranslation("translations");
  return (
    <Box
      as="a"
      px={2}
      py={1}
      rounded={'md'}
      _hover={{
        textDecoration: 'none',
        bg: useColorModeValue('gray.200', 'gray.700'),
      }}
      color={window.location.pathname === props.link ? "slategray" : "black"}
      backgroundColor={window.location.pathname === props.link ? "green.200" : ""}
      onClick={() => nav(props.link)}
    >
      {t(props.name)}
    </Box>
  )
} 

interface LayoutProps {
  children: JSX.Element;
  links: NavLinkProp[];
}

function Layout(props: LayoutProps) {
  const { t } = useTranslation("translations");
  const { isOpen, onOpen, onClose } = useDisclosure()
  const btnRef = React.useRef(null);
  const { user, setUser, setPermission } = useUserContext();
  const nav = useNavigate();
  const { colorMode } = useColorMode()

  const logout = () => {
    setUser(null);
    setPermission([]);

    localStorage.removeItem('token')
    nav("/login");
  }

  const isAdmin = useCallback(() => {
    return user?.roles.findIndex(x => x === "Admin") !== -1;
  }, [user]);

  return (
    <>
      <Box bg={useColorModeValue('gray.100', 'gray.900')} px={4} className="fixed top-0 w-[100%] z-50">
        <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
          <HStack spacing={4} alignItems={'center'}>
            <Button ref={btnRef} colorScheme={'teal'} onClick={onOpen}>
              <HamburgerIcon/>
            </Button>
            <HStack as={'nav'} spacing={4} display={{base: 'none', md: 'flex'}}>
              {props.links.map((link, index) => (
                <PermissionGuard key={`link_top_${index}`} permissions={link.permissions}>
                  <NavLink {...link}/>
                </PermissionGuard>
              ))}
            </HStack>
          </HStack>
          <Drawer
            isOpen={isOpen}
            onClose={onClose}
            btnRef={btnRef}
            colorMode={colorMode}
            links={props.links}
          />
          <Flex alignItems={'center'}>
            <Menu>
              <MenuButton
                as={Button}
                rounded={'full'}
                variant={'link'}
                cursor={'pointer'}
                minW={0}>
                <Avatar
                  size={'sm'}
                  src={"profile-placeholder.png"}
                  showBorder={true}
                />
              </MenuButton>
              <MenuList className="p-2">
                {
                  isAdmin() &&
                    <DropdownMenuItem
                        action={() => nav("/admin/companies")}
                        icon={<SettingsIcon color={"blue.500"}/>}
                        name={t("ADMIN_PANEL")}
                    />
                }
                <MenuDivider/>
                <DropdownMenuItem
                  action={logout}
                  icon={<ArrowBackIcon/>}
                  name={t("LOGOUT")}
                />
              </MenuList>
            </Menu>
          </Flex>
        </Flex>
      </Box>

      <Box p={4} mt={16}>
        {
          props.children
        }
      </Box>
    </>
  )
}

export default Layout;